/**
 * SearchResultRow Container Component
 *
 * Wraps SearchResultRowView with a stable onPress callback and renders the
 * backend-canonical `player.name` (SE-5469), which already includes name
 * particles (e.g. "van", "de") in full.
 * @module features/player-detail/components/TransferReportsSearchStep/SearchResultRow
 */

import { useCallback } from "react";

import SearchResultRowView from "./SearchResultRowView";

import type { PlayerSearchPlayerFragment } from "@/generated/graphql";

/** Props for SearchResultRowContainer */
export interface SearchResultRowContainerProps {
  /** The full player fragment for data extraction and press handling */
  readonly player: PlayerSearchPlayerFragment;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback fired when this player row is pressed */
  readonly onPlayerPress: (player: PlayerSearchPlayerFragment) => void;
}

/**
 * Container for a search result row that provides stable press handling
 * @param root0 - Component props
 * @param root0.player - Player fragment
 * @param root0.isDark - Dark mode flag
 * @param root0.onPlayerPress - Press callback receiving the player
 */
const SearchResultRowContainer = ({
  player,
  isDark,
  onPlayerPress,
}: SearchResultRowContainerProps) => {
  const handlePress = useCallback(() => {
    onPlayerPress(player);
  }, [onPlayerPress, player]);

  return (
    <SearchResultRowView
      name={player?.name ?? ""}
      position={player.positions?.[0]?.abbreviation ?? null}
      teamName={player.team?.name ?? player.teamName ?? null}
      isDark={isDark}
      onPress={handlePress}
    />
  );
};

SearchResultRowContainer.displayName = "SearchResultRowContainer";

export default SearchResultRowContainer;
